import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Heart, ImageIcon, KeyRound, Loader2, Plus, Trash2, Users } from "lucide-react";
import api from "@/lib/api";
import { Label, SectionTitle, formatApiError, ghostBtn, solidBtn } from "./shared";
import { RoomDetail } from "./RoomDetail";

const EMPTY = { client_name: "", event: "", pin: "", note: "" };

export const ProofingRooms = () => {
  const [rooms, setRooms] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = () => api.get("/admin/rooms").then((r) => setRooms(r.data)).catch(() => setRooms([]));

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.client_name.trim()) {
      toast.error("Give the room a client name.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/rooms", form);
      toast.success(`Room for ${data.client_name} created.`);
      setForm(EMPTY);
      await load();
      setActiveId(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (room) => {
    if (!window.confirm(`Delete the proofing room for "${room.client_name}"? Photos uploaded only to this room are removed too.`)) return;
    try {
      await api.delete(`/admin/rooms/${room.id}`);
      toast.success("Room deleted.");
      if (activeId === room.id) setActiveId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (activeId) {
    return <RoomDetail roomId={activeId} onBack={() => { setActiveId(null); load(); }} />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <section data-testid="admin-room-create-section" className="lg:col-span-4 border border-line bg-coal p-6 sm:p-8 h-fit">
        <SectionTitle icon={Plus}>New Proofing Room</SectionTitle>
        <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-mute mt-2">
          A private link your client opens to review &amp; favourite their frames
        </p>
        <div className="space-y-5 mt-6">
          <div>
            <Label>Client Name *</Label>
            <input data-testid="admin-room-input-client" className="field mt-1" placeholder="Aisha & Rohan" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
          </div>
          <div>
            <Label>Event / Shoot</Label>
            <input data-testid="admin-room-input-event" className="field mt-1" placeholder="Udaipur Wedding — Nov 2026" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} />
          </div>
          <div>
            <Label>Access PIN (optional, 4–6 digits)</Label>
            <input data-testid="admin-room-input-pin" className="field mt-1" placeholder="1234" inputMode="numeric" maxLength={6} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} />
          </div>
          <div>
            <Label>Note to client (optional)</Label>
            <textarea data-testid="admin-room-input-note" rows={3} className="field mt-1 resize-none" placeholder="Pick your 40 favourites for the album…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <button data-testid="admin-room-create-button" onClick={create} disabled={creating} className={`${solidBtn} w-full`}>
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create Room
          </button>
        </div>
      </section>

      <section className="lg:col-span-8 border border-line bg-coal p-6 sm:p-8">
        <SectionTitle icon={Users} count={rooms?.length}>Proofing Rooms</SectionTitle>
        {rooms === null ? (
          <div className="flex justify-center py-12"><Loader2 size={18} className="animate-spin text-amber" /></div>
        ) : rooms.length === 0 ? (
          <p className="font-mono text-[11px] text-mute mt-6">No rooms yet. Create one on the left, add frames, and share the private link.</p>
        ) : (
          <div className="mt-6 space-y-3" data-testid="admin-rooms-list">
            {rooms.map((room, i) => (
              <div key={room.id} data-testid={`admin-room-item-${i}`} className="border border-line/70 bg-ink p-4 sm:p-5 flex flex-wrap items-center gap-4 hover:border-ivory/30 transition-colors">
                <div className="flex-1 min-w-[180px]">
                  <p className="font-display text-base text-ivory tracking-wide flex items-center gap-2">
                    {room.client_name}
                    {room.pin && <KeyRound size={11} className="text-amber" aria-label="PIN protected" />}
                  </p>
                  <p className="font-mono text-[10px] text-mute uppercase tracking-[0.15em] mt-1">
                    {room.event || "Shoot"} · /proof/{room.code}
                  </p>
                </div>
                <div className="flex items-center gap-5 font-mono text-[11px] text-ash">
                  <span className="flex items-center gap-1.5" data-testid={`admin-room-photo-count-${i}`}><ImageIcon size={12} className="text-mute" /> {room.photo_count}</span>
                  <span className="flex items-center gap-1.5" data-testid={`admin-room-fav-count-${i}`}><Heart size={12} className={room.favorite_count ? "text-amber fill-amber" : "text-mute"} /> {room.favorite_count}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`admin-room-open-button-${i}`} onClick={() => setActiveId(room.id)} className={ghostBtn}>
                    Manage <ArrowRight size={12} />
                  </button>
                  <button data-testid={`admin-room-delete-button-${i}`} aria-label={`Delete room ${room.client_name}`} onClick={() => remove(room)} className="w-10 h-10 flex items-center justify-center border border-line text-ash hover:text-red-400 hover:border-red-400/50 transition-all">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
